import React, { Key, useEffect, useMemo, useRef, useState } from "react";

import { Col, Row } from "antd-latest";
import _ from "lodash";
import { Helmet } from "react-helmet";
import { useHistory, useLocation } from "react-router-dom";

import { DealReviewTab } from "manager/components/vero1/DealReviewTab";
import { CompactLumenTab } from "manager/components/vero1/DealReviewTab/CompactLumenTab";
import {
  resolveFactorCardDisplay,
  vero1CardNeutral,
  vero1CardVerdict,
} from "manager/components/vero1/vero1BannerState";
import { useRegisterVero1DealContext } from "manager/components/vero1/Vero1ReviewPanel";
import { useVero1Viewer } from "manager/components/vero1/vero1Viewer";
import {
  useCosignApplicationInvitation,
  useCosignApplications,
  useGetLatestLumenV2Run,
  useGetLumenV2RunById,
} from "manager/hooks/api";
import {
  useGetVero1Checklist,
  useVero1StatusSettling,
} from "manager/hooks/api/vero1Queries";
import {
  isDealApplicationsReadyForIdFraudReview,
  isOpenIdFraudReviewState,
  resolveIdFraudReviewNavigation,
} from "manager/pages/Deals/blockedByIdFraud";
import { Loader } from "shared/components/LargeLoader";
import Spacer from "shared/components/Spacer";
import { SIZES } from "shared/components/Spacer/constants";

import { LumenV2 } from "shared/components/vega/LumenV2";
import { VeroCopilot, VeroCopilotRef } from "shared/components/VeroCopilot";
import { useActiveCompany, usePermissions } from "shared/hooks";
import { useListApplicantsFromApplicationService } from "shared/hooks/api";

import ConditionallyProceedInfoBanner from "../../ConditionallyProceedInfoBanner";
import LedgerTable from "../../LedgerTable";
import { ActionBar } from "../action-bar";

import { AppRow, AppRowProps } from "../app-row";

import { AppTabs } from "../app-tabs";

import { DealActivityTab } from "../DealActivityTab";
import { DealLoadError } from "../DealLoadError";
import { DealNotFound } from "../DealNotFound";
import { Dependents } from "../dependents";
import { DealHeaderV2 } from "../header";
import {
  ApproveDealModalV2,
  CancelDealModalV2,
  DeclineDealModalV2,
  MoreInfoDealModal,
} from "../modals";
import { ResendToPmsModal } from "../modals/resend-to-pms-modal";
import { SendToEntrataModal } from "../modals/send-to-entrada-modal";

import { useRequestCountyRecords } from "../request-county-records/hook";
import {
  shouldDisableLink,
  shouldShowAction,
} from "../request-county-records/utils";

import { useAppSummary } from "../use-app-summary";
import {
  getLegacyAppForApplicant,
  getRawApplicationForApplicant,
} from "../use-app-summary.utils";

import { useDealSummaryRedesign } from "../useDealSummaryRedesign";
import { deriveCanInitiateCosign } from "../utils";

import { ApplicantProvidedNotice } from "./ApplicantProvidedNotice";
import ApplicantSummary from "./ApplicantSummary/ApplicantSummary";

import {
  ApplicantSummarySkeleton,
  FactorCardsSkeleton,
} from "./ApplicantSummarySkeleton";

import {
  getVero1CardOutcomes,
  vero1CardsGovern,
  Vero1CardOutcomes,
} from "./LumenSummary/helpers/vero1CardOutcomes";
import LumenSummary from "./LumenSummary/LumenSummary";
import { isBackgroundReportIncomplete } from "./queryHelpers";

export type AppListProps = {
  dealId: string;
  company: any;
};

const Space = () => (
  <>
    &nbsp;
    <Spacer size={SIZES.xs} />
  </>
);

export type AppListInnerProps = {
  apps?: AppRowProps[];
  refetchDeal?: () => void;
  loading?: boolean;
  skipId?: string;
};

export const AppListInner: React.FC<AppListInnerProps> = ({
  apps = [],
  refetchDeal,
  skipId,
  loading,
}) => {
  return (
    <Loader loading={loading}>
      {apps.map(
        (app) =>
          (!skipId || String(app.appId) !== String(skipId)) && (
            <AppRow {...app} refetchDeal={refetchDeal} />
          )
      )}
    </Loader>
  );
};

export const AppListV2: React.FC<AppListProps> = ({ dealId, company }) => {
  const copilotRef = useRef<VeroCopilotRef>(null);
  // an open VERO1 review is scoped to its deal — landing here on a different
  // deal discards it
  useRegisterVero1DealContext(dealId);
  const location = useLocation();
  const history = useHistory();
  const consumedIdFraudReview = useRef(false);
  const [autoOpenIdFraudApplicantId, setAutoOpenIdFraudApplicantId] = useState<
    string | null
  >(null);
  const {
    title,
    dealModalProps,
    headerProps,
    actionBarProps,
    apps = [],
    deps = [],
    loading,
    dealNotFound,
    dealFetchFailed,
    refetchDeal,
    contactAll,
    invite,
    countyRecordsProps,
    applicationAuditsCount,
    conditionalProceed,
    dealStatus,
    applicationVrn,
    applications,
    rawFullApplications = [],
    lumenRevision,
  } = useAppSummary(dealId, company);

  const { applicants, isLoading: isApplicantsLoading } =
    useListApplicantsFromApplicationService(applicationVrn);
  const applicantsList = Array.isArray(applicants) ? applicants : [];

  const dealApplicationsReady = isDealApplicationsReadyForIdFraudReview({
    applications,
    dealNotFound,
    dealFetchFailed,
  });

  useEffect(() => {
    if (consumedIdFraudReview.current || loading) {
      return;
    }
    // Applicants never load for a missing/failed deal; do not wait on that query.
    if (isApplicantsLoading && !(dealNotFound || dealFetchFailed)) {
      return;
    }
    if (!isOpenIdFraudReviewState(location.state)) {
      return;
    }

    const navigation = resolveIdFraudReviewNavigation({
      applicants: applicantsList.map((applicant) => ({
        id: applicant?.id,
        sentilink: getLegacyAppForApplicant(apps, applicant, applications)
          ?.sentilink,
      })),
      dealApplicationsReady,
    });
    if (navigation.action === "wait") {
      return;
    }
    if (navigation.action === "open") {
      setAutoOpenIdFraudApplicantId(navigation.applicantId);
    }
    consumedIdFraudReview.current = true;
    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [
    applicantsList,
    applications,
    apps,
    dealApplicationsReady,
    dealFetchFailed,
    dealNotFound,
    history,
    isApplicantsLoading,
    loading,
    location.hash,
    location.pathname,
    location.search,
    location.state,
  ]);

  const applicantOrder = _.chain([
    "primary-applicant",
    "applicant",
    "guarantor",
    "occupant",
  ])
    .invert()
    .mapValues(_.toSafeInteger)
    .value();

  const { canAccessSensitiveDealDataInLumen } = usePermissions();
  const isIdentityVerificationEnabled = apps?.some(
    (app) => app.identity?.enabled
  );

  const { showLumen } = actionBarProps || {};
  const useDealSummaryRedesignGate = useDealSummaryRedesign();
  // the strip owns the bands; a reopened deal must not sheet the prior run's
  const hideOutcomes =
    useDealSummaryRedesignGate || Boolean(actionBarProps?.vero1Active);

  const { canViewInternal } = useVero1Viewer();
  const factorCards = resolveFactorCardDisplay(
    actionBarProps?.vero1BannerState,
    Boolean(actionBarProps?.vero1Active),
    canViewInternal
  );

  const { checklist: vero1Checklist } = useGetVero1Checklist(
    dealId,
    Boolean(actionBarProps?.vero1Active)
  );
  const vero1Neutral = vero1CardNeutral(
    Boolean(actionBarProps?.vero1Active),
    canViewInternal
  );
  const vero1Verdict = actionBarProps?.vero1Active
    ? vero1CardVerdict(actionBarProps?.vero1BannerState, vero1Checklist?.status)
    : undefined;

  const { lumenRun } = useGetLatestLumenV2Run(applicationVrn, showLumen);
  const lumenRunIsDerived = Boolean((lumenRun as any)?.vero1Derived);
  const { lumenRun: baseLumenRun } = useGetLumenV2RunById(
    (lumenRun as any)?.vero1DerivedFrom,
    showLumen && lumenRunIsDerived
  );
  const applicantsLumenRun = lumenRunIsDerived
    ? baseLumenRun ?? null
    : lumenRun;
  // Cosign logic - enable if company has Cosign enabled
  const { activeCompany } = useActiveCompany();
  const primaryApp = apps?.find(
    (app) => (app as any).party === "primary-applicant"
  );
  const { cosignApplication } = useCosignApplications(
    primaryApp?.appId,
    {
      // Fetch existing ApplicationCosign even when property mapping was removed
      // (e.g. after dashboard disconnect — PRODUCT-1132 AC #3).
      enabled: Boolean(activeCompany?.enableCosign) && primaryApp?.appId,
    },
    false
  );
  // Allows the user to perform cosign actions:
  // 1. Company has Cosign enabled
  // 2. Existing Cosign application on the deal, OR property mapped and eligible for new invite
  const isDealDecisionMade = rawFullApplications.some(
    (app) => app.dealDecisionIsMade
  );
  const { cosignInvitationEligibility } = useCosignApplicationInvitation(
    primaryApp?.appId,
    {
      enabled:
        Boolean(activeCompany?.enableCosign) &&
        headerProps?.property?.hasCosignEnabled &&
        primaryApp?.appId,
    },
    false
  );
  const canInitiateCosign = deriveCanInitiateCosign({
    vero1Active: Boolean(actionBarProps?.vero1Active),
    isDealDecisionMade,
    cosignStatus: cosignApplication?.status,
    hasCosignEnabled: headerProps?.property?.hasCosignEnabled,
    eligibility: cosignInvitationEligibility,
  });
  const {
    openRequestCountyRecordsModal,
    isRequestCountyCriminalSearchLoading,
  } = useRequestCountyRecords(countyRecordsProps, refetchDeal);

  const countyCriminalSearchStatus =
    countyRecordsProps?.countyCriminalSearchStatus;
  const countyRecordsRequestable =
    shouldShowAction(countyCriminalSearchStatus?.value) &&
    !shouldDisableLink(
      countyCriminalSearchStatus,
      isRequestCountyCriminalSearchLoading
    );

  const isLumenStillCalculating = ["IS", "AI", "APS"].includes(dealStatus);
  const backgroundAndCreditChecksEnabled = Boolean(
    applicantsList.some(
      (applicant) => applicant.shouldRunBackgroundAndCreditChecks
    )
  );
  const vero1StatusSettling = useVero1StatusSettling(dealId);
  const vero1Governs = vero1CardsGovern(vero1Verdict);
  const vero1CardOutcomes = React.useMemo(
    () =>
      vero1Governs
        ? getVero1CardOutcomes(vero1Checklist?.items ?? [], {
            backgroundIncomplete: isBackgroundReportIncomplete(lumenRun),
          })
        : undefined,
    [vero1Governs, vero1Checklist, lumenRun]
  );
  const applicantCardOutcomesFor = React.useMemo(() => {
    const cache = new Map<string, Vero1CardOutcomes | undefined>();
    return (applicationId?: Key | null) => {
      if (!vero1Governs) return undefined;
      const key = applicationId == null ? "" : String(applicationId);
      if (!cache.has(key)) {
        cache.set(
          key,
          getVero1CardOutcomes(vero1Checklist?.items ?? [], {
            backgroundIncomplete: isBackgroundReportIncomplete(lumenRun),
            applicationId,
            applicantScope: true,
          })
        );
      }
      return cache.get(key);
    };
  }, [vero1Governs, vero1Checklist, lumenRun]);
  const factorCardSetSettling =
    (useDealSummaryRedesignGate &&
      ((loading && apps.length === 0) ||
        (isApplicantsLoading && applicantsList.length === 0))) ||
    (Boolean(actionBarProps?.vero1Active) && vero1StatusSettling);
  const primaryApplicantCosignBlockInfo = useMemo(() => {
    const legacyApp = _.find(apps, { applicantVrn: primaryApp?.applicantVrn });
    return {
      verifiedIncome: legacyApp?.income?.verifiedIncome,
      rent: legacyApp?.rent,
    };
  }, [apps, primaryApp?.applicantVrn]);

  if (dealNotFound) {
    return <DealNotFound />;
  }

  if (dealFetchFailed) {
    return <DealLoadError onRetry={refetchDeal} />;
  }

  return (
    <>
      <SendToEntrataModal {...dealModalProps} />
      <ResendToPmsModal {...dealModalProps} />
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {conditionalProceed && (
        <>
          <ConditionallyProceedInfoBanner />
          <Space />
        </>
      )}

      <DealHeaderV2
        deal={headerProps}
        refetchDeal={refetchDeal}
        contactAll={contactAll}
        invite={invite}
        requestCountyRecords={
          countyRecordsRequestable && openRequestCountyRecordsModal
        }
        companyHasCosignEnabled={activeCompany?.enableCosign}
      />
      <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
        <ActionBar
          {...actionBarProps}
          cardsDocked={Boolean(showLumen && factorCards.showCards)}
          lumenRun={lumenRun}
          refetch={refetchDeal}
          copilotRef={copilotRef}
          canInitiateCosign={canInitiateCosign}
          applicants={applicantsList}
          cosignApplication={cosignApplication}
          isLumenStillCalculating={isLumenStillCalculating}
          primaryApplicantCosignBlockInfo={primaryApplicantCosignBlockInfo}
          cosignEligibilityReasons={cosignInvitationEligibility?.reasons || []}
          loading={loading}
        />

        {showLumen && factorCards.showCards && factorCardSetSettling && (
          <FactorCardsSkeleton />
        )}
        {showLumen && factorCards.showCards && !factorCardSetSettling && (
          <LumenSummary
            lumenRun={lumenRun}
            dealId={dealId}
            vero1CardOutcomes={vero1CardOutcomes}
            hideOutcomes={hideOutcomes}
            applicants={applicantsList}
            isLumenStillCalculating={isLumenStillCalculating}
            backgroundAndCreditChecksEnabled={backgroundAndCreditChecksEnabled}
            isIdentityVerificationEnabled={isIdentityVerificationEnabled}
            applications={applications}
            lumenRevision={lumenRevision}
            monthlyRentDollars={headerProps?.rent}
          />
        )}

        <ApproveDealModalV2 {...dealModalProps} />
        <MoreInfoDealModal {...dealModalProps} />
        <DeclineDealModalV2 {...dealModalProps} />
        <CancelDealModalV2 {...dealModalProps} />
      </div>

      <AppTabs
        dealId={dealId}
        Activity={DealActivityTab}
        Lumen={
          // VERO1 deals drop the tab: agents read runs in the deal review
          // tab, and leasing must not reach raw runs (D13)
          actionBarProps?.vero1Active
            ? undefined
            : () =>
                useDealSummaryRedesignGate ? (
                  <CompactLumenTab
                    lumenRun={lumenRun}
                    detailed={canAccessSensitiveDealDataInLumen}
                  />
                ) : (
                  <LumenV2
                    lumenRun={lumenRun}
                    detailed={canAccessSensitiveDealDataInLumen}
                  />
                )
        }
        Ledger={() => <LedgerTable application={applicationVrn} />}
        DealReview={
          // VERO1-active deals only (ruled 2026-08-31): the screening-only
          // framing waits on a drawing — non-VERO1 deals get no tab in MVP
          actionBarProps?.vero1Active ? (
            <DealReviewTab
              dealId={dealId}
              vero1Active
              latestLumenRun={lumenRun}
              vero1BannerState={actionBarProps?.vero1BannerState}
            />
          ) : undefined
        }
        manualNotes={applicationAuditsCount?.numberOfManualNotes}
      >
        {loading &&
          (useDealSummaryRedesignGate ? (
            applicantsList.length === 0 && <ApplicantSummarySkeleton />
          ) : (
            <Loader loading={loading} />
          ))}
        <Row gutter={[0, 16]}>
          {Boolean(actionBarProps?.vero1Active) && (
            <Col span={24}>
              <ApplicantProvidedNotice />
            </Col>
          )}
          {_.chain(applicantsList)
            .sortBy((applicant) =>
              _.get(applicantOrder, applicant?.party, 9999)
            )
            .map((applicant) => {
              const { id } = applicant;
              const legacyApp = getLegacyAppForApplicant(
                apps,
                applicant,
                applications
              );
              const fullApplication = getRawApplicationForApplicant(
                rawFullApplications,
                applicant,
                legacyApp
              );
              return (
                <Col span={24} key={id}>
                  <ApplicantSummary
                    id={id}
                    lumenRun={applicantsLumenRun}
                    isLumenStillCalculating={isLumenStillCalculating}
                    legacyApp={legacyApp}
                    shouldFetchMissingLegacyApp={!loading}
                    fullApplication={fullApplication}
                    refetchDeal={refetchDeal}
                    dealId={dealId}
                    dealStatus={dealStatus}
                    cosignApplication={cosignApplication}
                    vero1Neutral={vero1Neutral}
                    vero1Verdict={vero1Verdict}
                    vero1CardOutcomes={applicantCardOutcomesFor(
                      fullApplication?.id
                    )}
                    legacySummaryPending={
                      useDealSummaryRedesignGate && loading && apps.length === 0
                    }
                    autoOpenIdVerificationModal={
                      autoOpenIdFraudApplicantId === String(id)
                    }
                  />
                </Col>
              );
            })
            .value()}

          <Col span={24}>
            <Dependents dependents={deps} />
          </Col>
        </Row>
      </AppTabs>
      <VeroCopilot
        ref={copilotRef}
        dealId={dealId}
        dealData={{
          dealStatus,
          applicationVrn,
          lumenRun,
          applicants: applicantsList,
          apps,
          deps,
        }}
      />
    </>
  );
};
